import { Box, Checkbox, Dropdown, Menu, MenuButton, Stack } from "@mui/joy";
import { useNavigate, useParams } from "@tanstack/react-router";
import groupBy from "lodash/groupBy";
import { useEffect, useMemo, useState } from "react";

import { Route } from "@/core/types";
import { useCompetitionQuery, useCurrentCompetitionUuid } from "@/hooks/competitions";
import { useCompetitionRoutesQuery } from "@/hooks/routes";
import { useCurrentVenueQuery, useCurrentVenueUuid } from "@/hooks/venues";

import { useCompetitionContext } from "../CompetitionOverview/CompetitionProvider";
import Map from "../Map/Map";
import { MapRoutePinSvgRaw } from "../Map/MapRoutePinSvg";
import { MapProgress } from "../Map/MapsList";

const SelectMapDropdown = ({
  maps,
  selectedMapUuids,
  onChangeSelection,
}: {
  maps: { uuid: string; name: string }[];
  selectedMapUuids: string[];
  onChangeSelection: (mapUuids: string[]) => void;
}) => {
  return (
    <Stack
      direction="row"
      gap={0.5}
      sx={{
        position: "absolute",
        top: 8,
        right: 8,
        zIndex: 1000,
        ".MuiIconButton-root, .MuiMenuButton-root": {
          backgroundColor: "primary.700",
          border: "1px solid",
          borderColor: "var(--joy-palette-primary-800)!important",
          "&:hover, &[aria-pressed=true]": {
            backgroundColor: "var(--joy-palette-primary-600)",
          },
        },
      }}
    >
      <Dropdown>
        <MenuButton variant="solid" color="neutral" size="sm">
          Map config
        </MenuButton>
        <Menu
          placement="bottom-end"
          sx={{
            backgroundColor: "primary.700",
            borderColor: "primary.800",
          }}
        >
          {maps.map((map) => (
            <Box key={map.uuid} sx={{ overflow: "hidden" }}>
              <Checkbox
                checked={selectedMapUuids.includes(map.uuid)}
                size="sm"
                onChange={(e) => {
                  if (e.target.checked) {
                    onChangeSelection([...selectedMapUuids, map.uuid]);
                  } else {
                    onChangeSelection([...selectedMapUuids].filter((uuid) => uuid !== map.uuid));
                  }
                }}
                label={map.name}
                slotProps={{
                  root: {
                    sx: {
                      paddingX: 1,
                      paddingY: 0.5,
                      display: "flex",
                    },
                  },
                  label: {
                    sx: {
                      color: "var(--joy-palette-primary-200)",
                    },
                  },
                  checkbox: {
                    sx: {
                      backgroundColor: "var(--joy-palette-primary-900)",
                      "&:hover": {
                        backgroundColor: "var(--joy-palette-primary-800)",
                        borderColor: "var(--joy-palette-primary-800)",
                      },
                      borderColor: "var(--joy-palette-primary-800)",
                    },
                  },
                }}
              />
            </Box>
          ))}
        </Menu>
      </Dropdown>
    </Stack>
  );
};

export default function CompetitionRoutesMaps() {
  const { selectedCategoryUuid } = useCompetitionContext();

  const venueUuid = useCurrentVenueUuid();
  const competitionUuid = useCurrentCompetitionUuid();

  const { data: competition } = useCompetitionQuery(competitionUuid, venueUuid);

  const isDynamicNumbering = competition?.settings?.hasDynamicRouteNumbering;

  const { data: routes, isPending: routesPending } = useCompetitionRoutesQuery(
    competitionUuid,
    venueUuid,
  );
  const routesEmpty = !routes || !routes.length;
  const { data: venue, isPending: venueIsPending } = useCurrentVenueQuery();

  const routesWithDisplay = useMemo(() => {
    return routes?.map((route) => {
      const routeGroup = selectedCategoryUuid
        ? route.competitionRouteGroups.find((x) => x.categories.includes(selectedCategoryUuid))
        : undefined;
      const routeGroupDisplay = routeGroup
        ? route.displayRouteGroupMapping[routeGroup?.uuid]
        : undefined;

      return {
        ...route,
        displayNumber: isDynamicNumbering ? routeGroupDisplay?.number || " " : undefined,
        isFilteredOut:
          selectedCategoryUuid &&
          !route.competitionRouteGroups.some((crg) =>
            crg.categories.includes(selectedCategoryUuid),
          ),
      };
    });
  }, [routes, selectedCategoryUuid, isDynamicNumbering]);

  console.log({ filteredRoutes: routesWithDisplay });

  const venueMapsByUuid = useMemo(() => {
    return Object.fromEntries(venue?.maps.map((map) => [map.uuid, map]) || []);
  }, [venue?.maps]);

  const routesByMap = useMemo(() => {
    return (
      groupBy(
        routesWithDisplay?.filter((r) => !!r.map && !!r.mapPosition),
        (route) => route.map,
      ) || {}
    );
  }, [routesWithDisplay]) as Record<
    string,
    (Route & {
      map: string;
      mapPosition: { x: number; y: number };
      displayNumber?: string;
      isFilteredOut: boolean;
    })[]
  >;

  const [displayMapUuids, setDisplayMapUuids] = useState<string[]>([]);
  const displayMaps = useMemo(() => {
    return displayMapUuids.map((mapUuid) => venueMapsByUuid[mapUuid]);
  }, [displayMapUuids, venueMapsByUuid]);

  const selectedRouteUuid = useParams({ strict: false }).routeUuid;
  const navigate = useNavigate();

  const selectRoute = (routeUuid: string) => {
    navigate({
      from: "/gym/$venueUuid/events/$competitionUuid/routes",
      to: "/gym/$venueUuid/events/$competitionUuid/routes/$routeUuid",
      replace: true,
      params: (prev) => ({ ...prev, routeUuid }),
    });
  };

  useEffect(() => {
    if (routesEmpty && (routesPending || venueIsPending)) {
      return;
    }
    let mapUuids = Object.keys(routesByMap);
    if (!mapUuids.length && venue?.maps.length) {
      mapUuids = venue!.maps
        .filter((map) => map.available)
        .map((map) => map.uuid)
        .slice(0, 1);
    }
    if (!displayMapUuids.length && venue?.maps && mapUuids.length) {
      setDisplayMapUuids(mapUuids.slice(0, 2).map((mapUuid) => venueMapsByUuid[mapUuid].uuid));
    }
  }, [
    routesEmpty,
    displayMapUuids.length,
    routesByMap,
    venue,
    venueMapsByUuid,
    routesPending,
    venueIsPending,
  ]);

  return (
    <Stack
      direction="row"
      sx={{
        backgroundColor: "primary.blue2",
        height: "100%",
        position: "relative",
        overflowX: "auto",
      }}
    >
      <SelectMapDropdown
        maps={venue?.maps.filter((x) => !!routesByMap[x.uuid]) || []}
        selectedMapUuids={displayMapUuids}
        onChangeSelection={setDisplayMapUuids}
      />
      {displayMaps.length === 0 && routesPending ? <MapProgress /> : null}
      {displayMaps.map((map) => {
        const mapRoutes = routesByMap[map.uuid] || [];

        return (
          <Box
            sx={{
              flex: 1,
              position: "relative",
              borderLeft: "1px solid #FFFFFF44",
              "&:first-of-type": {
                borderLeft: "none",
              },
              minWidth: 200,
            }}
            key={map.uuid}
          >
            <Map
              brushEnabled={false}
              map={map}
              mapObjects={mapRoutes}
              zoomToBbox={false}
              bboxPadding={10}
              maxZoom={10}
              idAccessor={(route) => route.uuid}
              selectedIds={new Set()}
              onSetSelectedIds={() => null}
              renderMapObject={(route, transform, _isSelected) => {
                return (
                  <g
                    transform={`translate(${route.mapPosition.x}, ${route.mapPosition.y})`}
                    key={route.uuid}
                    onClick={() => selectRoute(route.uuid)}
                  >
                    <g
                      transform={`scale(${
                        (route.isFilteredOut
                          ? 0.5
                          : selectedRouteUuid
                            ? route.uuid === selectedRouteUuid
                              ? 1.5
                              : 0.75
                            : 1) / Math.min(transform.k, 4)
                      })`}
                      opacity={route.isFilteredOut ? 0.75 : 1}
                    >
                      <MapRoutePinSvgRaw
                        route={route}
                        dragToMoveEnabled={false}
                        disableTagAlert
                        hideText={isDynamicNumbering && !selectedCategoryUuid}
                      />
                    </g>
                  </g>
                );
              }}
            />
            {routesPending ? <MapProgress /> : null}
          </Box>
        );
      })}
    </Stack>
  );
}
